// रूप साधना: a 3 × 2 verb table (पुरुष × वचन, two numbers only) with hidden cells. Each hidden cell has its own
// small radio group of forms below the table; the chosen form appears in the cell. After checking, every hidden
// cell shows whether it was right, and the right form; a विकल्प (variant) answer also shows its standard form.

import type { FC, ReactNode } from 'react'
import type { AnswerResponse, QuestionBody } from '@/prakrit/components/practice/practice-types'

import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'
import { NUMBER_LABEL, PERSON_LABEL } from '@/prakrit/lib/grammar/labels'
import { Bi } from '@/prakrit/components/common/bi'
import { StatusBadge } from '@/prakrit/components/common/status-badge'
import { moveBetween } from '@/prakrit/components/lesson/steps/step-ui'

type Table = Extract<QuestionBody, { kind: 'table' }>['table']

interface TableQuestionProps {
    table: Table
    value: Record<string, string>
    onChange: (slot: string, option: string) => void
    locked: boolean
    result: AnswerResponse | null
    kids: boolean
}

export const TableQuestion: FC<TableQuestionProps> = ({ table, value, onChange, locked, result, kids }): ReactNode => {
    const slotById = new Map(table.slots.map(s => [s.id, s]))
    const resultOf = (slot: string) => result?.slotResults?.find(r => r.id === slot)

    return (
        <div className='flex flex-col gap-5'>
            <div className='overflow-x-auto rounded-2xl border border-border bg-card shadow-card'>
                <table className='w-full min-w-[19rem] border-collapse text-left'>
                    <caption className='border-b border-border px-4 py-3 text-left'>
                        <span lang='hi' data-ex='hi' className='hindi block font-semibold'>{table.title.hi}</span>
                        <span lang='en' data-ex='en' className='block text-sm text-muted-foreground'>{table.title.en}</span>
                    </caption>
                    <thead>
                        <tr className='bg-surface-muted'>
                            <th scope='col' className='px-3 py-2 text-sm font-semibold'>
                                <span className='sr-only'>पुरुष · person</span>
                            </th>
                            {NUMBERS.map(n => (
                                <th key={n} scope='col' className='px-3 py-2'>
                                    <Bi hi={NUMBER_LABEL[n].hi} en={NUMBER_LABEL[n].en} hiClassName='text-sm font-semibold' enClassName='text-xs font-normal' always />
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {PERSONS.map(p => (
                            <tr key={p} className='border-t border-border'>
                                <th scope='row' className='px-3 py-2 align-middle'>
                                    <Bi hi={PERSON_LABEL[p].hi} en={PERSON_LABEL[p].en.split(' (')[0]} hiClassName='text-sm font-semibold' enClassName='text-xs font-normal' always />
                                </th>
                                {NUMBERS.map(n => {
                                    const cell = table.cells.find(c => c.person === p && c.number === n)

                                    if (!cell) return <td key={n} />

                                    const slot = cell.slot ? slotById.get(cell.slot) : undefined
                                    const chosenId = slot ? value[slot.id] : undefined
                                    const chosen = slot?.options.find(o => o.id === chosenId)?.pra
                                    const verdict = slot ? resultOf(slot.id) : undefined

                                    return (
                                        <td key={n} className='px-2 py-2 align-middle'>
                                            <div
                                                className={cn(
                                                    'flex min-h-tap flex-col justify-center gap-0.5 rounded-lg px-3 py-1.5',
                                                    !slot && 'bg-background/40',
                                                    slot && !verdict && 'border-2 border-dashed border-saffron/60 bg-saffron/5',
                                                    verdict?.correct && 'border-2 border-success bg-success-soft',
                                                    verdict && !verdict.correct && 'border-2 border-danger bg-danger-soft'
                                                )}
                                            >
                                                <span lang='pra' className='text-xs text-muted-foreground'>{cell.subject}</span>
                                                {slot ? (
                                                    <span className='flex flex-wrap items-baseline gap-x-2'>
                                                        <span lang='pra' className={cn('pra', kids ? 'pra-md' : 'pra-sm', !chosen && 'text-muted-foreground')}>
                                                            {chosen ?? (
                                                                <>
                                                                    <span aria-hidden='true'>?</span>
                                                                    <span lang='hi' className='sr-only'>खाली · empty</span>
                                                                </>
                                                            )}
                                                        </span>
                                                        {verdict && (
                                                            <span className='sr-only'>{verdict.correct ? 'सही · correct' : 'ग़लत · wrong'}</span>
                                                        )}
                                                        {verdict && !verdict.correct && (
                                                            <span className='inline-flex items-center gap-1'>
                                                                <Check className='size-4 shrink-0 text-success' aria-hidden='true' />
                                                                <span className='sr-only'>सही उत्तर · correct answer:</span>
                                                                <span lang='pra' className={cn('font-prakrit font-semibold text-success', kids ? 'pra-md' : 'pra-sm')}>{verdict.expected}</span>
                                                            </span>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span lang='pra' className={cn('pra', kids ? 'pra-md' : 'pra-sm')}>{cell.form}</span>
                                                )}
                                            </div>
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className='flex flex-col gap-4'>
                {table.slots.map((slot, si) => {
                    const verdict = resultOf(slot.id)

                    return (
                        <fieldset key={slot.id} className='flex flex-col gap-2'>
                            <legend className='mb-1 flex flex-wrap items-center gap-2 text-sm font-semibold'>
                                <span aria-hidden='true' className='flex size-6 items-center justify-center rounded-full bg-saffron/15 text-xs text-saffron-strong'>{si + 1}</span>
                                <span lang='hi' className='hindi'>{slot.label.hi}</span>
                                <span aria-hidden='true' className='text-muted-foreground'>·</span>
                                <span lang='en' className='font-normal text-muted-foreground'>{slot.label.en}</span>
                                {verdict && (verdict.correct ? (
                                    <>
                                        <Check className='size-4 text-success' aria-hidden='true' />
                                        <span className='sr-only'>सही · correct</span>
                                    </>
                                ) : (
                                    <>
                                        <X className='size-4 text-danger' aria-hidden='true' />
                                        <span className='sr-only'>ग़लत · wrong</span>
                                    </>
                                ))}
                                {verdict?.vikalp && (
                                    <span className='inline-flex flex-wrap items-center gap-1.5 font-normal'>
                                        <StatusBadge kind='alt' />
                                        <span className='text-xs text-muted-foreground'>
                                            <span lang='hi' className='hindi'>सामान्य रूप</span>
                                            <span aria-hidden='true'> · </span>
                                            <span lang='en'>standard form</span>
                                            <span aria-hidden='true'>: </span>
                                        </span>
                                        <span lang='pra' className='pra pra-sm'>{verdict.vikalp}</span>
                                    </span>
                                )}
                            </legend>
                            <div role='radiogroup' aria-label={`${slot.label.hi} · ${slot.label.en}`} onKeyDown={event => moveBetween(event)} className='flex flex-wrap gap-2'>
                                {slot.options.map(option => {
                                    const selected = value[slot.id] === option.id
                                    const right = Boolean(result) && verdict?.expectedId === option.id

                                    return (
                                        <button
                                            key={option.id}
                                            type='button'
                                            role='radio'
                                            aria-checked={selected}
                                            aria-disabled={locked || undefined}
                                            data-option
                                            onClick={() => {
                                                if (!locked) onChange(slot.id, option.id)
                                            }}
                                            className={cn(
                                                'inline-flex min-h-tap items-center rounded-xl border-2 px-4 py-1.5 shadow-sm transition-colors',
                                                selected && !result && 'border-saffron bg-saffron/10',
                                                !selected && !right && 'border-border bg-card hover:border-saffron/60 hover:bg-saffron/5',
                                                result && selected && verdict?.correct && 'border-success bg-success-soft',
                                                result && selected && !verdict?.correct && 'border-danger bg-danger-soft',
                                                result && !selected && right && 'border-dashed border-success bg-card',
                                                locked && !selected && !right && 'opacity-60'
                                            )}
                                        >
                                            <span lang='pra' className='pra pra-md'>{option.pra}</span>
                                        </button>
                                    )
                                })}
                            </div>
                        </fieldset>
                    )
                })}
            </div>
        </div>
    )
}
